import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
  type StatAccessQueryScope,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import { statAccessListPath } from '~/hooks/analyticsStatAccess/statAccessApiPaths';
import type { AnalyticsReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';

import type { AnalyticsStatCategory } from '../analyticsStatCategory';

export type StaffStatAccessEntry = {
  statKey: string;
  statName: string;
  category: AnalyticsStatCategory;
  hasLimit: boolean;
  /** 'all' | 'none' | 'custom' */
  columnLayoutMode: 'all' | 'none' | 'custom';
  columnCount?: number | null;
  limitView: {
    id: number;
    publicId: string;
    updatedAt: string;
    updatedBy: string | null;
  } | null;
};

export type StaffStatsAccessResponse = {
  analyticsReportingMode: AnalyticsReportingMode | null;
  /** Optional parent mode for staff-on-child warning (Ergon may add later). */
  parentAnalyticsReportingMode?: AnalyticsReportingMode | null;
  constraintAccountId?: number;
  stats: StaffStatAccessEntry[];
};

const fetchStaffStatsAccess = async (
  accountType: string,
  accountId: string,
  scope?: StatAccessQueryScope,
): Promise<StaffStatsAccessResponse> => {
  const { data } = await customFetch.get<StaffStatsAccessResponse>(
    statAccessListPath(accountType, accountId, scope?.parentAccountId),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      params: staffConfigModeQueryParams(scope?.analyticsReportingMode),
    },
  );
  return data;
};

export const getStaffStatsAccessQueryOptions = (
  accountType: string,
  accountId: string,
  scope?: StatAccessQueryScope,
) =>
  queryOptions({
    queryKey: [
      'staff',
      'stats-access',
      accountType,
      accountId,
      scope?.parentAccountId ?? null,
      staffConfigModeQueryKeyPart(scope?.analyticsReportingMode),
    ],
    queryFn: () => fetchStaffStatsAccess(accountType, accountId, scope),
    enabled: !!accountType && !!accountId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useStaffStatsAccessQuery = (
  accountType: string,
  accountId: string,
  scope?: StatAccessQueryScope,
) =>
  // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
  useQuery(getStaffStatsAccessQueryOptions(accountType, accountId, scope));
